import { WIDGET_CONTROL_TYPE } from '@examdojo/core/widget';
import type { GeometryPadWidgetConfig } from '../geometrypad.model';

/**
 * Right-angle triangle with interactive θ selection.
 *
 * Click on either non-right angle to set it as θ.
 * Sides are labeled as Opposite, Adjacent, and Hypotenuse relative to θ.
 */
export const RIGHT_ANGLE_EXPLANATION_PRESET: GeometryPadWidgetConfig = {
  id: 'right-angle-explanation',
  type: 'geometrypad',
  params: {
    elements: {
      t1: {
        type: 'Triangle',
        params: {
          c: 5,
          a: 3,
          gamma: 90,
        },
        names: {
          a: 'Opposite',
          b: 'Adjacent',
          c: 'Hypotenuse',
          alpha: 'θ',
        },
      },
    },
  },
  display: {
    coordinateSystem: {
      boundingbox: {
        xStart: -1,
        xEnd: 8,
        yStart: -1.5,
        yEnd: 6,
      },
    },
    geometry: {
      shape: {
        coloring: false,
      },
      angle: {
        rightAngleDisplay: 'square',
        coloring: false,
        fill: true,
      },
      label: {
        coloring: true,
      },
    },
    elements: {
      t1: {
        subparts: {
          alpha: {
            angle: {
              strokeColor: 'sky',
              fillColor: 'sky',
            },
            label: {
              color: 'sky',
            },
          },
          beta: {
            visible: false,
            angle: {
              strokeColor: 'sky',
              fillColor: 'sky',
            },
            label: {
              visible: true,
              text: 'click to switch',
              color: 'sky',
              variant: 'tertiary',
            },
          },
          gamma: {
            label: {
              visible: false,
            },
          },
          BC: {
            line: {
              color: 'sky',
            },
            label: {
              color: 'sky',
            },
          },
          CA: {
            line: {
              color: 'pink',
            },
            label: {
              color: 'pink',
            },
          },
        },
      },
    },
    canvas: {
      color: 'neutral-50',
    },
  },
  behavior: {
    controls: [
      {
        id: 'cs-theta-a',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'θ',
        location: {
          x: 't1.point.A.x',
          y: 't1.point.A.y',
        },
        group: 'theta-select',
        selected: true,
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            't1.angle.alpha': {
              visible: true,
              angle: {
                strokeColor: 'sky',
                fillColor: 'sky',
              },
              label: {
                visible: true,
                text: 'Reference angle θ',
                color: 'sky',
                variant: 'secondary',
              },
            },
            't1.angle.beta': {
              visible: false,
              angle: {
                strokeColor: 'sky',
                fillColor: 'sky',
              },
              label: {
                visible: true,
                text: 'click to switch',
                color: 'sky',
                variant: 'tertiary',
              },
            },
            't1.edge.BC': {
              line: {
                color: 'sky',
              },
              label: {
                visible: true,
                color: 'sky',
                text: 'Opposite',
              },
            },
            't1.edge.CA': {
              line: {
                color: 'pink',
              },
              label: {
                visible: true,
                color: 'pink',
                text: 'Adjacent',
              },
            },
            't1.edge.AB': {
              label: {
                visible: true,
                text: 'Hypotenuse',
              },
            },
          },
        },
      },
      {
        id: 'cs-theta-b',
        type: WIDGET_CONTROL_TYPE.CanvasSelection,
        label: 'θ',
        location: {
          x: 't1.point.B.x',
          y: 't1.point.B.y',
        },
        group: 'theta-select',
        trigger: {
          action: 'PATCH_DISPLAY',
          payload: {
            't1.angle.alpha': {
              visible: false,
              angle: {
                strokeColor: 'sky',
                fillColor: 'sky',
              },
              label: {
                visible: true,
                text: 'click to switch',
                color: 'sky',
                variant: 'tertiary',
              },
            },
            't1.angle.beta': {
              visible: true,
              angle: {
                strokeColor: 'sky',
                fillColor: 'sky',
              },
              label: {
                visible: true,
                text: 'Reference angle θ',
                color: 'sky',
                variant: 'secondary',
              },
            },
            't1.edge.BC': {
              line: {
                color: 'pink',
              },
              label: {
                visible: true,
                color: 'pink',
                text: 'Adjacent',
              },
            },
            't1.edge.CA': {
              line: {
                color: 'sky',
              },
              label: {
                visible: true,
                color: 'sky',
                text: 'Opposite',
              },
            },
            't1.edge.AB': {
              label: {
                visible: true,
                text: 'Hypotenuse',
              },
            },
          },
        },
      },
    ],
  },
};
